<template>
  <v-card class="pa-2 mb-4">
    <v-toolbar class="text-h5">Current Reservations</v-toolbar>
    <v-layout column>
      <v-data-table
        :headers="headers"
        :items="reservations"
        :loading="loading"
        hideDefaultFooter
        fluid
        no-data-text="There are no current reservations for this tenant"
      >
        <template slot="item" slot-scope="props">
          <tr>
          <td class="py-1">{{ props.item.type === "1" ? "Lead" : "Reservation" }}</td>
          <td class="py-1">{{ props.item.promotion ? props.item.promotion.text : "No promotion" }}</td>
          <td class="py-1">${{ parseFloat(props.item.quoted_price).toFixed(2) }}</td>
          <td class="py-1">{{ props.item.move_in_date ? formatDateUTC(props.item.move_in_date) : "—" }}</td>
          <td class="py-1">{{ props.item.followup_at ? formatDate(props.item.followup_at) : "—" }}</td>
          <td class="py-1">{{ props.item.last_followup_at ? formatDate(props.item.last_followup_at) : "—" }}</td>
          <td class="py-1">{{ props.item.created_at ? formatDate(props.item.created_at) : "—" }}</td>
          <td class="py-1">{{ props.item.customer_source }}</td>
          <td class="py-1">
            <v-layout column>
              <v-btn
                data-testid="tenant-current-reservation-update-btn"
                class="tenant-current-reservation-update-btn info"
                small
                @click.stop="openManageModal(props.item)"
              >
                Update
              </v-btn>
              <v-btn
                data-testid="tenant-current-reservation-convert-btn"
                class="tenant-current-reservation-convert-btn primary"
                small
                @click.stop="handleConversion(props.item)"
              >
                Convert
              </v-btn>
            </v-layout>
          </td>
          </tr>
        </template>
      </v-data-table>
    </v-layout>
    <convert-lead
      :convertLeadModal="convertLeadModal"
      :lead="lead"
      :unitFeatures="unitFeatures"
      @collapse="closeConvertLeadModal"
      @convertToRental="convertToRental($event)"
      @addField="addField($event)"
    />
    <manage-reservation
      v-model="manageModal"
      :reservation="reservation"
      :facility="facility"
      :manageModal="manageModal"
      :unitFeatures="unitFeatures"
      @collapse="closeManageModal"
      @reservationUpdated="reservationUpdated($event)"
    />
    <rent-unit
      :rentUnitModal="rentUnitModal"
      :reservation="reservation"
      :unitFeatures="unitFeatures"
      :tpp="tpp"
      :updateNeeded="updateNeeded"
      :availableUnits="availableUnits"
      :rentUnitStep="rentUnitStep"
      @currentStep="setRentUnitStep($event)"
      @unitUpdated="unitUpdated($event)"
      @getMoveInCost="getMoveInCost($event)"
      @collapse="closeRentUnitModal"
    />
  </v-card>
</template>

<script>
/* eslint-disable eqeqeq */
import ConvertLead from "@/components/convert-lead/ConvertLead";
import ManageReservation from "@/components/ManageReservation";
import RentUnit from "@/components/rent-unit/RentUnit";
import { dateMethodMixins } from "@/mixins/dateMethodMixins";
import moment from "moment";
import { tenantReservationsHeaders } from "@/helpers/tenant/tenant-reservations-data-table-headers";
import { tenantStatusOptions } from "@/helpers/tenant/tenant-status-options";

export default {
  props: {
    availableUnits: {
      type: Array,
      required: true
    },
    facility: {
      type: Object,
      required: true
    },
    ledgerId: {
      type: [String, Number]
    },
    reservations: {
      type: Array,
      required: true
    }
  },

  mixins: [dateMethodMixins],

  data() {
    return {
      loading: false,
      infoLoading: false,
      unitFeaturesLoading: false,

      convertLeadModal: false,
      createReservationModal: false,
      rentUnitModal: false,
      manageModal: false,
      moveInSelect: false,
      followUpSelect: false,

      lead: "",
      reservation: "",

      unit: "",
      unitFeatures: "",
      tpp: [],
      updateNeeded: false,
      rentUnitStep: 0,

      headers: tenantReservationsHeaders,
      statusOptions: tenantStatusOptions
    };
  },

  methods: {
    openConvertLeadModal(dealData) {
      let payload = {
        facilityId: this.$route.params.facilityId,
        unit_id: dealData.unit_id,
        concession_id: dealData.concession_id
      };
      if (dealData.unit_id) {
        this.getUnitFeatures(payload);
      }
      this.$store.commit("updateInfoValue", { key: "waiting_id", value: dealData.waiting_list_id });
      this.lead = dealData;
      this.convertLeadModal = true;
    },

    openManageModal(unit) {
      let payload = {
        facilityId: this.$route.params.facilityId,
        unit_id: unit.unit_id,
        concession_id: unit.concession_id ? unit.concession_id : null
      };
      this.reservation = unit;
      this.getUnitFeatures(payload);
      this.getPromotionOptions(payload);
      this.manageModal = true;
    },

    closeManageModal() {
      this.manageModal = false;
      this.reservation = {};
      this.$store.commit("updateInfoValue", { key: "waiting_id", value: null });
    },

    closeRentUnitModal() {
      this.rentUnitModal = false;
      this.$store.commit("updateInfoValue", { key: "waiting_id", value: null });
      this.$store.commit("setDeferPromotion", false);
    },

    closeConvertLeadModal() {
      this.convertLeadModal = false;
      this.lead = "";
      this.$store.commit("updateInfoValue", { key: "waiting_id", value: null });
    },

    handleConversion(reservation) {
      reservation.type === "1"
        ? this.openConvertLeadModal(reservation)
        : this.openRentUnitModal(reservation);
    },

    convertToRental(reservation) {
      this.$store.commit("setLeadConversion");
      this.openRentUnitModal(reservation);
    },

    openRentUnitModal(dealData) {
      this.$store.dispatch("getMerchandise", this.$route.params.facilityId);
      this.rentUnitModal = true;
      if (!dealData.unit_id && !dealData.unit_id_first_available) {
        this.rentUnitStep = 0;
        this.$store.dispatch("handleAlert", "We cannot locate the original reservation for this customer. " +
                    "Please inform the customer and use the provided list to select an available unit.");
      } else {
        const concessionId = dealData.ConcessionID || dealData.concession_id || null;

        let payload = {
          facilityId: this.$route.params.facilityId,
          unit_id: dealData.unit_id || dealData.unit_id_first_available,
          concession_id: dealData.concession_id || dealData.concession_id,
          move_in_date: moment(this.unit.move_in_date).format("YYYY-MM-DD"),
          waiting_id: dealData.waiting_list_id
        };
        if (dealData.waiting_list_id) {
          this.$store.commit("updateInfoValue", { key: "waiting_id", value: dealData.waiting_list_id });
        }
        this.selectedPromotion = dealData.concession_id;
        this.$store.commit("setUnit", dealData);
        this.$store.commit("updatePromotion", parseFloat(concessionId));
        this.$store.dispatch("getSimilarUnits", payload);
        this.$store.dispatch("getMoveInInstructions", this.$route.params.facilityId);
        this.$store.dispatch("handleQuotedRate", dealData);
        this.getUnitFeatures(payload)
          .then(resp => {
            if (resp.rented) {
              this.updateNeeded = true;
              this.$store.dispatch("handleAlert", "The unit originally tied to this reservation has been " +
                                "rented. Please inform the customer and use the provided list to select an available unit.");
              this.rentUnitModal = true;
              this.rentUnitStep = 0;
            } else {
              this.getMoveInCost(payload);
              this.getPromotionOptions(payload);
              this.getTenantProtectionPlans();
              this.rentUnitStep = 1;
            }
          });
      }
    },

    setRentUnitStep(step) {
      this.rentUnitStep = step;
    },

    getMoveInCost(payload) {
      this.$store.dispatch("getMoveInCost", payload)
        .then(resp => {
          if (resp.data.message == "Invalid Concession Plan ID") {
            payload.concession_id = null;
            return this.getMoveInCost(payload);
          }
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    async getUnitFeatures(payload) {
      this.unitFeaturesLoading = true;
      return this.$store.dispatch("getUnitFeatures", payload)
        .then(resp => {
          this.unitFeatures = resp.data;
          this.unitFeaturesLoading = false;
          return this.unitFeatures;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.unitFeaturesLoading = false;
        });
    },

    getTenantProtectionPlans() {
      this.$store.dispatch("getTenantProtectionPlans", this.$route.params.facilityId)
        .then(resp => {
          this.tpp = resp.data;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    getPromotionOptions(payload) {
      this.$store.dispatch("getPromotionOptions", payload);
    },

    addField(payload) {
      this.lead[payload.field] = payload.value;
    },

    setUnit(unit) {
      this.reservation = unit;
    },

    unitUpdated(unit) {
      const concessionId = unit.concession_id || null;
      let payload = {
        facilityId: this.$route.params.facilityId,
        unit_id: unit.unit_id_first_available,
        concession_id: concessionId,
        move_in_date: moment().format("MM/DD/YYYY")
      };
      this.$store.commit("updatePromotion", concessionId);
      this.$store.dispatch("getSimilarUnits", payload);
      this.getMoveInCost(payload);
      this.getUnitFeatures(payload);
      this.getPromotionOptions(payload);
      this.getTenantProtectionPlans();
    },

    reservationUpdated(unit) {
      this.getUnitFeatures(unit);
      this.getPromotionOptions(unit);
      this.$emit("reservationUpdated");
    }
  },

  computed: {
    moveInDate: {
      get() {
        return this.reservation.move_in_date ? moment(this.reservation.move_in_date).format("YYYY-MM-DD") : "";
      },
      set(newVal) {
        this.reservation.move_in_date = newVal;
      }
    },

    followupDate: {
      get() {
        return this.reservation.followup_at ? moment(this.reservation.followup_at).format("YYYY-MM-DD") : "";
      },
      set(newVal) {
        this.reservation.followup_at = newVal;
      }
    }
  },

  components: {
    ConvertLead,
    ManageReservation,
    RentUnit
  }
};
</script>
